// src/components/CardAgenda.js
import React from "react";
import PropTypes from "prop-types";
import { getUser } from "../utils/auth"
import { isZh } from "../utils/language"
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";

function decodeText(raw) {
  if (!raw) return raw;
  let s = String(raw).trim();
  const decodeHtmlEntities = (str) => {
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = str;
      return txt.value;
    } catch (e) {
      return str;
    }
  };
  const decodeNumericEntities = (str) => str.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
  const decodeUnicodeEscapes = (str) =>
    str.replace(/\\\\u/g, "\\u").replace(/\\u([\dA-Fa-f]{4})/g, (_, g) => String.fromCharCode(parseInt(g, 16)));
  s = decodeHtmlEntities(s);
  s = decodeNumericEntities(s);
  s = decodeUnicodeEscapes(s);
  s = s.replace(/\u0000/g, "").replace(/\uFFFD/g, "");
  return s;
}

/* check if current login user is present in any agenda_personnels.umats for this event */
function isUserRelatedToEvent(ev) {
  try {
    const user = getUser?.();
    if (!user || !user.umat) return false;

    const userId = user.umat.documentId || user.umat.id;
    if (!userId) return false;

    const personnels = ev?.agenda_book?.agendaPersonnels;
    if (!personnels || !Array.isArray(personnels.GroupRole)) return false;

    for (const g of personnels.GroupRole) {
      const roles = g?.ServiceRole || [];
      for (const r of roles) {

        const internal = r?.InternalUmat || [];
        for (const u of internal) {
          if (!u) continue;
          if (String(u.refId) === String(userId)) return true;
        }

        const external = r?.ExternalUmat || [];
        for (const u of external) {
          if (!u) continue;
          if (String(u.refId) === String(userId)) return true;
        }
      }
    }

    return false;
  } catch (e) {
    return false;
  }
}

/**
 * CardAgenda
 * - displays area badge (if any) before subject
 * - clickable: calls onOpen(ev)
 */
export default function CardAgenda({ ev, onOpen }) {
  const areaLabel = ev?.area?.shortname 
                    || ev?.fotang_external?.fotangNameCH
                    || ev?.externalDanWeiOtherDesc

  let areaCat = null; // akan null kalau tidak ada label sama sekali

  if (areaLabel === ev?.fotang_external?.fotangNameCH ||
      areaLabel === ev?.externalDanWeiOtherDesc) {
    // Diambil dari fotang_external atau externalDanWeiOtherDesc
    areaCat = 3;
  } else if (ev?.area) {
    // Diambil dari area => cek outerRegion pada area.region
    const outer = ev.area?.region?.outerRegion;
    // treat only explicit true as kategori 2; null/false => kategori 1
    areaCat = (outer === true) ? 2 : 1;
  }

  //                  console.log("Agenda: ", ev)

  const relatedToUser = isUserRelatedToEvent(ev);

  return (
    <>
    <style>{`
      .event-row {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .event-subject-mobile {
        padding-left: 0.5rem;
      }

      /* desktop tetap inline */
      @media (min-width: 768px) {
        .event-row {
          flex-direction: row;
          align-items: center;
        }

        .event-subject-mobile {
          padding-left: 0;
        }
      }
    `}</style>
    <div
      className="event-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(ev)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen?.(ev);
      }}
      aria-label={`Open agenda ${decodeText(ev.subject)}`}
      style={{
        borderTop: "1px solid #f0f3f8",
        padding: "0.5rem 0",
        cursor: "pointer",
      }}
    >
      <div className="event-row">
        {areaLabel ? (
          <span
            className={`area-badge mobile-full ${
              areaCat === 2
                ? "area-cat2" // luar kota
                : areaCat === 3
                ? "area-cat3" // fotang eksternal
                : "area-cat1" // default (lokal)
            }`} 
            style={{
              padding: "0.25rem 0.5rem",
              borderRadius: "0.5rem",
              fontSize: "0.875rem"
              }}
          >
            { isZh() ? decodeText(areaLabel) : capitalizePinyin(toPinyinSync(areaLabel)) }
          </span>
        ) : null}
        <div className="event-subject event-subject-mobil" 
            style={{
              marginLeft: "0.25rem",
              fontSize: "1rem",
              fontWeight: 400,
              background: relatedToUser ? "#fff8df" : "transparent", // yellow highlight
              padding: relatedToUser ? "0.125rem 0.375rem" : undefined,
              borderRadius: relatedToUser ? "0.25rem" : undefined,
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
            }}
        >
          {isZh() ? decodeText(ev.subject_zh) : ev.subject}
          {relatedToUser && (
            <span
              aria-hidden="true"
              title="Anda terdaftar di agenda ini"
              style={{ color: "#dc2626", fontWeight: 800, fontSize: "1rem", lineHeight: 1 }}
            >
              *
            </span>
          )}
        </div>
      </div>

      <div
        className="event-meta"
        style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.25rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
      >
        {ev.hoursInfo ? <span className="meta-item" style={{ background: "#f5f7fb", padding: "0.1875rem 0.5rem", borderRadius: "0.75rem" }}>{decodeText(ev.hoursInfo)}</span> : null}
        {ev.agenda_master ? (
          <span className="meta-item" style={{ background: "#f5f7fb", padding: "0.1875rem 0.5rem", borderRadius: "0.75rem" }}>
            {decodeText(ev.agenda_master.displayName || ev.agenda_master.name || "")}
          </span>
        ) : null}
      </div>
    </div>
    </>
  );
}

CardAgenda.propTypes = {
  ev: PropTypes.object.isRequired,
  onOpen: PropTypes.func,
};
